<script setup>
import { useHttpErrorsHandler } from "../../Composables/httpErrorsHandler.js";
import { useVehicleConfigurator } from "../../Composables/useVehicleConfigurator.js";
import { useToast } from "vue-toast-notification";
import { useRouter } from "vue-router";
import { computed, inject, onBeforeMount, onMounted, reactive, ref } from "vue";
import Loader from "../../Components/Loader.vue";
import FormTextField from "../../Components/FormTextField.vue";
import FormSwitch from "../../Components/FormSwitch.vue";
import FormDatePicker from "../../Components/FormDatePicker.vue";
import FormSelectField from "../../Components/FormSelectField.vue";
import FormTextarea from "../../Components/FormTextarea.vue";
import { useAuthUserStore } from "../../Store/AuthUserStore.js";
import FormPhoneField from "../../Components/FormPhoneField.vue";
import FormSelectCountry from "../../Components/FormSelectCountry.vue";
import FormSelectCenters from "../../Components/FormSelectCenters.vue";
import axios from "axios";
import FormVehicleConfigurator from "../../Components/FormVehicleConfigurator.vue";

const { httpErrorsHandler } = useHttpErrorsHandler();
const brandConfig = inject('brandConfig');
const authUserStore = useAuthUserStore();
const authUser = computed(() => authUserStore.authUser);
const $toast = useToast();
const router = useRouter();

const loading = ref(false)
const centerPreSelected = ref(null)
const errors = ref(null)
const form = reactive( {
    gender: null,
    first_name: null,
    last_name: null,
    company_name: null,
    email: null,
    phone_number: null,
    phone_number_2: null,
    birth_date: null,
    subscribed_to_newsletter: false,
    notes: null,
    last_contact: null,
    preferred_contact_method: null,
    client_status: 'prospect',
    referral_source: 'Comptoir',
    recontact_required: false,
    recontact_reason: null,
    vehicle_type: null,
    vehicle_brand_id: null,
    vehicle_model_id: null,
    vehicle_version_id: null,
    vehicle_engine_id: null,
    centers: [],
    address: {
        address_line_1: null,
        address_line_2: null,
        postal_code: null,
        city: null,
        country: null,
    }
})

const {
    brands: carBrands,
    models: carModels,
    versions: carVersions,
    engines: carEngines,
    setInitialized,
} = useVehicleConfigurator(form, {
    onError: httpErrorsHandler,
})

onMounted(() => {
    setInitialized();
})

onBeforeMount(() => {
    if (authUser.value.centers.length >= 1) {
        centerPreSelected.value = {
            data: {
                id: authUser.value.centers[0].id,
                name: authUser.value.centers[0].name
            }
        }
    }
})

function createClient() {
    loading.value = true;
    let formData = new FormData();

    Object.entries(form).forEach(([key, value]) => {
        if (key === 'address') {
            if (Object.values(value).every(x => (x === null || x === ''))) {
                return;
            }
            else {
                Object.entries(value).forEach(([itemKey, itemValue]) => {
                    if (itemValue !== null && itemValue !== '') {
                        formData.append(`${key}[${itemKey}]`, itemValue);
                    }
                });
                return;
            }
        }
        if (key === 'centers') {
            return value.forEach((center) => {
                formData.append(`${key}[]`, center.id);
            });
        }

        if (typeof value === 'boolean') {
            return formData.append(key, value ? 1 : 0);
        }
        if (value !== null && value !== '') {
            formData.append(key, value);
        }
    });

    axios.post("/api/clients", formData)
        .then(response => {
            $toast.success('Client ajouté avec succès', {
                position: 'top',
                duration: 5000,
                dismissible: true,
                pauseOnHover: true,
            });
            router.push({name: 'clients.show', params: {id: response.data.data.id}});
        })
        .catch(error => {
            errors.value = error.response.data.errors;
            httpErrorsHandler(error);
            loading.value = false;
        });
}

</script>

<template>
<div>
    <div class="row">
        <div class="col-lg-12">
            <!--Loader-->
            <div class="card" v-if="loading">
                <div class="card-body">
                    <loader/>
                </div>
            </div>
        </div>
    </div>
    <!--Form-->
    <form v-on:submit.prevent="createClient">
        <div class="row" v-if="!loading">
            <div class="col-lg-6">
                <!--Principal infos-->
                <div class="card">
                <div class="card-header align-items-center d-flex">
                    <h4 class="card-title mb-0 flex-grow-1"><i class="mdi mdi-account-box me-2 align-middle fs-4"></i>Informations principales</h4>
                </div>
                <div class="card-body">
                    <div class="row">
                        <form-select-field
                            name="gender"
                            label="Civilité"
                            :label-hidden="false"
                            @update:field="form.gender = $event"
                            :errors="errors"
                            :required="true"
                            :data="form.gender"
                            bs-class="col-xl-2"
                            :disabled="false"
                            :liste-options="[
                                { text: 'M.', value: 'man' },
                                { text: 'Mme', value: 'woman' }
                            ]"
                        />

                        <form-text-field
                            name="first_name"
                            label="Prénom"
                            :label-hidden="false"
                            @update:field="form.first_name = $event"
                            :errors="errors"
                            :required="true"
                            :data="form.first_name"
                            bs-class="col-xl-5"
                        />

                        <form-text-field
                            name="last_name"
                            label="Nom"
                            :label-hidden="false"
                            @update:field="form.last_name = $event"
                            :errors="errors"
                            :required="true"
                            :data="form.last_name"
                            bs-class="col-xl-5"
                        />

                        <form-select-field
                            name="client_status"
                            label="Statut"
                            :label-hidden="false"
                            @update:field="form.client_status = $event"
                            :errors="errors"
                            :required="true"
                            :data="form.client_status"
                            bs-class="col-xl-6"
                            :disabled="false"
                            :liste-options="[
                                { text: 'Autre', value: 'other' },
                                { text: 'Client', value: 'client' },
                                { text: 'Client douteux', value: 'client_doubtful' },
                                { text: 'Fournisseur', value: 'supplier' },
                                { text: 'Prospect', value: 'prospect' },
                                { text: 'Spam', value: 'spam' },
                                { text: 'Opportunité', value: 'opportunity' },
                            ]"
                        />

                        <form-phone-field
                            name="phone_number"
                            label="Téléphone"
                            :label-hidden="false"
                            @update:field="form.phone_number = $event"
                            :errors="errors"
                            :required="true"
                            bs-class="col-xl-6"
                            :data="form.phone_number"
                        />


                        <form-select-centers
                            :errors="errors"
                            label="Centre(s)"
                            name="centers"
                            :required="true"
                            :data="form.centers"
                            @update:field="form.centers = $event"
                            bs-class="col-xl-12"
                            :disabled="false"
                            :taggable="false"
                            :label-hidden="false"
                            placeholder="Sélectionnez un ou des centres"
                        />

                        <form-text-field
                            name="email"
                            label="Email"
                            :label-hidden="false"
                            @update:field="form.email = $event"
                            :errors="errors"
                            :required="false"
                            :data="form.email"
                            bs-class="col-xl-6"
                            type="email"
                        />

                        <form-phone-field
                            name="phone_number_2"
                            label="Téléphone secondaire"
                            :label-hidden="false"
                            @update:field="form.phone_number_2 = $event"
                            :errors="errors"
                            :required="false"
                            bs-class="col-xl-6"
                            :data="form.phone_number_2"
                        />

                        <form-text-field
                            name="company_name"
                            label="Société"
                            :label-hidden="false"
                            @update:field="form.company_name = $event"
                            :errors="errors"
                            :required="false"
                            :data="form.company_name"
                            bs-class="col-xl-12"
                            type="text"
                        />
                    </div>
                </div>
            </div>
                <!--Secondary infos-->
                <div class="card">
                    <div class="card-header align-items-center d-flex">
                        <h4 class="card-title mb-0 flex-grow-1"><i class="mdi mdi-information-outline me-2 align-middle fs-4"></i>Informations secondaires</h4>
                    </div>
                    <div class="card-body">
                        <div class="row">

                            <form-select-field
                                name="referral_source"
                                label="Origine du contact"
                                :label-hidden="false"
                                @update:field="form.referral_source = $event"
                                :errors="errors"
                                :required="true"
                                :data="form.referral_source"
                                bs-class="col-xl-12"
                                :disabled="false"
                                :liste-options="[
                                    { text: 'Comptoir', value: 'Comptoir' },
                                    { text: 'Événement', value: 'Événement' },
                                    { text: 'RingOver', value: 'RingOver' },
                                    { text: 'Influenceur', value: 'Influenceur' },
                                    { text: 'Réseaux sociaux', value: 'Réseaux sociaux' },
                                    { text: 'ADS', value: 'ADS' },
                                    { text: 'Site web', value: 'Site web' },
                                    { text: 'Site web - ChatBot', value: 'Site web - ChatBot' },
                                    { text: 'Bouche-à-oreille', value: 'Bouche à oreille' },
                                    { text: 'Démarchage', value: 'Démarchage' },
                                    { text: 'Achat Lead', value: 'Achat Lead' },
                                ]"
                            />

                            <!--<form-select-field-->
                            <!--    name="preferred_contact_method"-->
                            <!--    label="Méthode de contact préférée"-->
                            <!--    :label-hidden="false"-->
                            <!--    @update:field="form.preferred_contact_method = $event"-->
                            <!--    :errors="errors"-->
                            <!--    :required="false"-->
                            <!--    :data="form.preferred_contact_method"-->
                            <!--    bs-class="col-xl-6"-->
                            <!--    :disabled="false"-->
                            <!--    :liste-options="[-->
                            <!--        { text: 'Téléphone', value: 'Téléphone' },-->
                            <!--        { text: 'Email', value: 'Email' },-->
                            <!--        { text: 'SMS', value: 'SMS' }-->
                            <!--    ]"-->
                            <!--/>-->

                            <form-date-picker
                                name="birth_date"
                                label="Date de naissance"
                                :label-hidden="false"
                                @update:field="form.birth_date = $event"
                                :errors="errors"
                                :required="false"
                                :data="form.birth_date"
                                bs-class="col-xl-6"
                                :disabled="false"
                            />

                            <form-date-picker
                                name="last_contact"
                                label="Dernier contact"
                                :label-hidden="false"
                                @update:field="form.last_contact = $event"
                                :errors="errors"
                                :required="false"
                                :data="form.last_contact"
                                bs-class="col-xl-6"
                                :disabled="false"
                            />

                            <form-textarea
                                name="notes"
                                label="Notes"
                                :label-hidden="false"
                                @update:field="form.notes = $event"
                                :errors="errors"
                                :required="false"
                                :data="form.notes"
                                bs-class="col-xl-12"
                                :row="2"
                            />

                            <form-switch
                                name="subscribed_to_newsletter"
                                label="Inscrit à la newsletter ?"
                                :label-hidden="false"
                                @update:field="form.subscribed_to_newsletter = $event"
                                :errors="errors"
                                :required="false"
                                :data="form.subscribed_to_newsletter"
                                bs-class="col-xl-12"
                                :disabled="false"
                            />

                            <form-switch
                                name="recontact_required"
                                label="Client à recontacter ?"
                                :label-hidden="false"
                                @update:field="form.recontact_required = $event"
                                :errors="errors"
                                :required="false"
                                :data="form.recontact_required"
                                bs-class="col-xl-12"
                                :disabled="false"
                            />

                            <form-text-field
                                v-if="form.recontact_required"
                                name="recontact_reason"
                                label="Raison de recontact"
                                :label-hidden="false"
                                @update:field="form.recontact_reason = $event"
                                :errors="errors"
                                :required="false"
                                :data="form.recontact_reason"
                                bs-class="col-xl-12"
                                type="text"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div class="col-lg-6">
                <!--Vehicle-->
                <div class="card">
                    <div class="card-header align-items-center d-flex">
                        <h4 class="card-title mb-0 flex-grow-1"><i class="mdi mdi-car me-2 align-middle fs-4"></i>Véhicule principal</h4>
                    </div>
                    <div class="card-body">
                        <div class="row">
                            <form-vehicle-configurator
                                :form="form"
                                :errors="errors"
                                :brands="carBrands"
                                :models="carModels"
                                :versions="carVersions"
                                :engines="carEngines"
                                :show-detailed-link="false"
                            />
                        </div>
                    </div>
                </div>


                <!--address-->
                <div class="card">
                    <div class="card-header align-items-center d-flex">
                        <h4 class="card-title mb-0 flex-grow-1"><i class="mdi mdi-map-marker-outline me-2 align-middle fs-4"></i>Adresse</h4>
                    </div>
                    <div class="card-body">
                        <div class="row">
                            <form-text-field
                                name="address.address_line_1"
                                label="Adresse"
                                :label-hidden="false"
                                @update:field="form.address.address_line_1 = $event"
                                :errors="errors"
                                :required="false"
                                :data="form.address.address_line_1"
                                bs-class="col-xl-12"
                            />

                            <form-text-field
                                name="address.address_line_2"
                                label="Complément d'adresse"
                                :label-hidden="false"
                                @update:field="form.address.address_line_2 = $event"
                                :errors="errors"
                                :required="false"
                                :data="form.address.address_line_2"
                                bs-class="col-xl-12"
                            />

                            <form-text-field
                                name="address.postal_code"
                                label="Code postal"
                                :label-hidden="false"
                                @update:field="form.address.postal_code = $event"
                                :errors="errors"
                                :required="false"
                                :data="form.address.postal_code"
                                bs-class="col-xl-4"
                            />

                            <form-text-field
                                name="address.city"
                                label="Ville"
                                :label-hidden="false"
                                @update:field="form.address.city = $event"
                                :errors="errors"
                                :required="false"
                                :data="form.address.city"
                                bs-class="col-xl-4"
                            />

                            <form-select-country
                                name="address.country"
                                label="Pays"
                                :label-hidden="false"
                                @update:field="form.address.country = $event"
                                :errors="errors"
                                :required="true"
                                :data="form.address.country"
                                bs-class="col-xl-4"
                            />
                            <div class="d-flex gap-2 justify-content-end">
                                <button type="button" class="btn btn-soft-dark" @click="router.back()">Retour</button>
                                <button type="submit" class="btn btn-secondary">Valider</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </form>
</div>
</template>

<style scoped>

</style>
